import { useEffect, useState } from 'react';
import googleIcon from 'assets/images/social_icons/googleIcon.svg';
import microsoftIcon from 'assets/images/social_icons/microsoftIcon.svg';
import appleIcon from 'assets/images/social_icons/appleIcon.svg';
import { Box, Typography, Divider } from '@mui/material';
import { useLocalization } from '@assetron/common/hooks/useLocalization';
import {
  FULLSTOP,
  RETURN_URL_QUERY_PARAM,
  AssetronBannerTypes,
  INVITE_DETAILS,
} from '@assetron/common/constants/constant';
import useAuth from '@assetron/common/hooks/useAuth';
import useDocumentTitle from '@assetron/web-common/components/common/useDocumentTitle';
import HeaderFooterLayout from '@assetron/web-common/layout/HeaderFooterLayout';
import AssetronButton from '@assetron/web-common/components/common/AssetronButton';
import useAssetronFooterStyles from '@assetron/web-common/components/common/styles/useAssetronFooterStyles';
import FullscreenLoader from '@assetron/web-common/components/common/FullscreenLoader';
import useConfig from '@assetron/common/hooks/useConfig';
import { PENDING_CREDENTIAL } from '@assetron/web-common/constants/LocalStorageConstants';
import useGoogleOneTap from 'auth/src/hooks/useGoogleOneTap';
import useFetchRedirectResult from 'auth/src/utils/auth-helper/useFetchRedirectResult';
import useSignUpLandingStyles from './styles/useSignUpLandingStyles';
import { useNavigation } from '../../../navigation/useNavigation';
import AssetronAlert from '@assetron/web-common/components/common/AssetronAlert';
import { GA_EVENTS } from '@assetron/common/constants/googleAnalyticsEvents';
import { trackGaEvent } from '@assetron/common/utils/googleAnalytics';
import { billingFlowLog } from '@assetron/web-common/utils/billingFlowDiagnostics';

function SignupLanding() {
  const styles = useSignUpLandingStyles();

  const { termsOfServiceLink, privacyPolicyLink } = useAssetronFooterStyles();
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLocalization();
  const { navigateToSignUpDetails, navigateToSignInLanding } = useNavigation();
  const {
    authWithGoogle,
    authWithMicrosoft,
    authWithApple,
    isUserAuthenticated,
    auth,
  } = useAuth();

  const { showOneTap } = useGoogleOneTap();

  useDocumentTitle(t('signUpLandingPage.pageTitle'));

  const { APP_URL } = useConfig();

  // Get return URL from query parameters instead of localStorage
  const urlParams = new URLSearchParams(globalThis.location.search);
  const returnUrlFromParams = urlParams.get(RETURN_URL_QUERY_PARAM);
  const appURL = returnUrlFromParams ?? APP_URL;

  // Read organization name from URL parameters
  const orgNameFromUrl = urlParams.get('org_name');
  const inviteDetails = globalThis.sessionStorage.getItem(INVITE_DETAILS);
  let organizationName: string | null = null;
  if (orgNameFromUrl) {
    try {
      organizationName = decodeURIComponent(orgNameFromUrl);
    } catch {
      organizationName = orgNameFromUrl; // Fallback to raw value
    }
  } else if (inviteDetails) {
    try {
      const parsed = JSON.parse(inviteDetails) as { orgName?: string };
      organizationName = parsed.orgName?.trim() ?? null;
    } catch {
      organizationName = null;
    }
  }

  const { fetchRedirectResult } = useFetchRedirectResult({
    appURL,
    setIsLoading,
    showOneTap,
  });

  useEffect(() => {
    fetchRedirectResult();
  }, [auth, fetchRedirectResult]);

  if (isUserAuthenticated || isLoading) {
    return <FullscreenLoader />;
  }

  const isPendingCred = !!globalThis.sessionStorage.getItem(PENDING_CREDENTIAL);

  return (
    <HeaderFooterLayout>
      <Box sx={styles.root}>
        {organizationName && (
          <Box sx={styles.alertContainer}>
            <AssetronAlert
              message={t('signUpLandingPage.inviteReadyMessage', {
                organizationName,
              })}
              type={AssetronBannerTypes.INFO}
            />
          </Box>
        )}
        {isPendingCred && (
          <Box sx={styles.alertContainer}>
            <AssetronAlert
              message={t('signInLandingPage.useSameMethodMessage')}
              type={AssetronBannerTypes.WARNING}
            />
          </Box>
        )}
        <Typography variant="large_heading_30_semibold">
          {t('signUpLandingPage.signUpHeading')}
        </Typography>
        <Box sx={styles.buttonContainer}>
          <Box sx={styles.socialIdProviderButtonContainer}>
            <AssetronButton
              size="md"
              hierarchy="secondary"
              dataTestId="signup-social-google-button"
              iconLeading={
                <Box component="img" loading="lazy" src={googleIcon} />
              }
              onClick={() => {
                trackGaEvent(GA_EVENTS.AUTH_SIGNUP_SOCIAL_CLICK, {
                  provider: 'google',
                });
                authWithGoogle();
              }}
            >
              <Typography variant="body_14_medium" sx={styles.googleSignUpText}>
                {t('signUpLandingPage.signUpWithGoogle')}
              </Typography>
            </AssetronButton>

            <AssetronButton
              size="md"
              hierarchy="secondary"
              dataTestId="signup-social-microsoft-button"
              iconLeading={
                <Box component="img" loading="lazy" src={microsoftIcon} />
              }
              onClick={() => {
                trackGaEvent(GA_EVENTS.AUTH_SIGNUP_SOCIAL_CLICK, {
                  provider: 'microsoft',
                });
                authWithMicrosoft();
              }}
            >
              <Typography variant="body_14_medium" sx={styles.googleSignUpText}>
                {t('signUpLandingPage.signUpWithMicrosoft')}
              </Typography>
            </AssetronButton>
            <AssetronButton
              size="md"
              hierarchy="secondary"
              dataTestId="signup-social-apple-button"
              iconLeading={
                <Box component="img" loading="lazy" src={appleIcon} />
              }
              onClick={() => {
                trackGaEvent(GA_EVENTS.AUTH_SIGNUP_SOCIAL_CLICK, {
                  provider: 'apple',
                });
                authWithApple();
              }}
            >
              <Typography variant="body_14_medium" sx={styles.googleSignUpText}>
                {t('signUpLandingPage.signUpWithApple')}
              </Typography>
            </AssetronButton>
          </Box>
          <Divider>
            <Typography
              variant="subheading_16_regular"
              sx={styles.dividerTextStyle}
            >
              {t('signUpLandingPage.or')}
            </Typography>
          </Divider>
          <Box sx={styles.createAccountButtonContainer}>
            <AssetronButton
              hierarchy="primary"
              size="md"
              onClick={() => {
                billingFlowLog('SignupLanding', 'signup_started', {
                  source: 'signup_landing',
                });
                trackGaEvent(GA_EVENTS.AUTH_SIGNUP_CREATE_ACCOUNT_CLICK, {
                  source: 'signup_landing',
                });
                navigateToSignUpDetails();
              }}
              dataTestId="signup-create-account-button"
            >
              <Typography
                sx={styles.createAccountButtonTextStyle}
                variant="body_14_medium"
              >
                {t('signUpLandingPage.createAccount')}
              </Typography>
            </AssetronButton>
            <Typography
              sx={styles.termAndPolicyTextStyle}
              variant="body_12_regular"
              component="div"
            >
              {t('signUpLandingPage.policyText1')}
              <AssetronButton
                hierarchy="link-color"
                link={termsOfServiceLink}
                newTab
              >
                {t('footer.termsOfService')}
              </AssetronButton>
              {t('signUpLandingPage.and')}
              <AssetronButton
                hierarchy="link-color"
                link={privacyPolicyLink}
                newTab
              >
                {t('footer.privacyPolicy')}
              </AssetronButton>
              {FULLSTOP}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.signinButtonContainer}>
          <Typography variant="body_14_regular">
            {t('signUpLandingPage.haveAnAccount')}
          </Typography>
          <AssetronButton
            hierarchy="link-color"
            size="lg"
            onClick={() => {
              trackGaEvent(GA_EVENTS.AUTH_NAVIGATE_SIGNIN_CLICK, {
                destination: 'signin',
              });
              navigateToSignInLanding();
            }}
          >
            <Typography variant="body_14_medium" sx={styles.signinText}>
              {t('signUpLandingPage.signIn')}
            </Typography>
          </AssetronButton>
        </Box>
      </Box>
    </HeaderFooterLayout>
  );
}

export default SignupLanding;
